// Type-only: the generated client's runtime module drags in axios, which the
// jest setup cannot load, and this module has no need for runtime values.
import type {
  ClearanceReview,
  FixSovRunSummary,
  ReportClearanceEntry,
} from "../../../../client";
import type { ReportStatus } from "../../../../api/types";

/** Statuses before a clearance decision, where the automation is relevant. */
export const PRE_CLEARANCE_STATUSES: ReadonlySet<ReportStatus> =
  new Set<ReportStatus>(["pre_underwriting_review", "blocked"]);

export type StageState =
  | { kind: "not_run" }
  | { kind: "in_progress" }
  | { kind: "done" };

type JobLike = { integration_type: string; finished_at: string | null };

export function stageState(
  latestJob: JobLike | undefined,
  stored: FixSovRunSummary | ClearanceReview | null | undefined
): StageState {
  if (latestJob && !latestJob.finished_at) return { kind: "in_progress" };
  if (stored) return { kind: "done" };
  return { kind: "not_run" };
}

export type BadgeTone = "running" | "clear" | "attention" | "muted";

export type ClearanceBadge = { label: string; tone: BadgeTone };

/**
 * The one-line badge under a pre-clearance status. Whatever is running wins;
 * otherwise the review result; otherwise whether the SOV fix finished.
 * `null` when nothing has happened yet, so the cell stays clean.
 */
export function clearanceBadge(
  fixSov: StageState,
  review: StageState,
  entry: ReportClearanceEntry | null | undefined
): ClearanceBadge | null {
  if (fixSov.kind === "in_progress") {
    return { label: "Fixing SOV", tone: "running" };
  }
  if (review.kind === "in_progress") {
    return { label: "Reviewing", tone: "running" };
  }
  const storedReview = entry?.review ?? null;
  if (storedReview) {
    switch (storedReview.outcome) {
      case "clear":
        return { label: "Clear", tone: "clear" };
      case "skipped":
        return { label: "Review skipped", tone: "muted" };
      case "needs_review": {
        const passed = storedReview.checks.filter(
          (c) => c.status === "pass"
        ).length;
        return {
          label: `${passed}/${storedReview.checks.length} checks`,
          tone: "attention",
        };
      }
    }
  }
  const storedFix = entry?.fix_sov ?? null;
  if (storedFix) {
    if (storedFix.completed) return { label: "SOV fix done", tone: "muted" };
    if (storedFix.end_reason === "no_attachments") {
      return { label: "SOV fix skipped", tone: "muted" };
    }
    return { label: "SOV fix failed", tone: "attention" };
  }
  return null;
}

/** One line describing a finished fix_sov run. */
export function fixSovHeadline(summary: FixSovRunSummary): string {
  if (summary.error) return "Crashed";
  if (!summary.completed) {
    return summary.end_reason === "no_attachments"
      ? "Skipped: no attachments"
      : `Did not complete (${summary.end_reason ?? "unknown"})`;
  }
  // Summaries stored before reupload_succeeded existed only know whether
  // reupload_sov was called.
  const succeeded = summary.reupload_succeeded ?? summary.reupload_invoked;
  if (succeeded) return "Completed, corrected SOV re-uploaded";
  return summary.reupload_invoked
    ? "Completed, re-upload failed"
    : "Completed, no re-upload";
}
